// ============================================================================
// Générateur pseudo-aléatoire de Python (refonte lot 5.7 bis, moteur v29).
// ============================================================================
// Le Mersenne Twister MT19937 et les fonctions du module `random` exactement comme
// CPython 3 : avec la même graine (`random.seed(4)`), `randint`, `random`, `choice`,
// `randrange`, `uniform`, `shuffle` donnent les MÊMES valeurs que Python. Le simulateur
// pas à pas rejoue donc un programme aléatoire comme l'ordinateur de l'élève.
// Les feuilles de trace, elles, imposent les tirages (énoncé : « on suppose que
// randint renvoie 4, puis 1 ») : cf. `draws` dans interp.ts.
// ============================================================================

const N = 624;
const M = 397;
const MATRIX_A = 0x9908b0df;
const UPPER = 0x80000000;
const LOWER = 0x7fffffff;

export class MersenneTwister {
  private readonly mt = new Uint32Array(N);
  private mti = N + 1;

  constructor(seed: bigint = 0n) {
    this.seed(seed);
  }

  private initGenrand(s: number): void {
    const mt = this.mt;
    mt[0] = s >>> 0;
    for (let i = 1; i < N; i++) {
      const p = mt[i - 1]!;
      mt[i] = (Math.imul(1812433253, p ^ (p >>> 30)) + i) >>> 0;
    }
    this.mti = N;
  }

  private initByArray(key: number[]): void {
    const mt = this.mt;
    this.initGenrand(19650218);
    let i = 1;
    let j = 0;
    for (let k = Math.max(N, key.length); k > 0; k--) {
      const p = mt[i - 1]!;
      mt[i] = ((mt[i]! ^ Math.imul(p ^ (p >>> 30), 1664525)) + key[j]! + j) >>> 0;
      i++;
      j++;
      if (i >= N) {
        mt[0] = mt[N - 1]!;
        i = 1;
      }
      if (j >= key.length) j = 0;
    }
    for (let k = N - 1; k > 0; k--) {
      const p = mt[i - 1]!;
      mt[i] = ((mt[i]! ^ Math.imul(p ^ (p >>> 30), 1566083941)) - i) >>> 0;
      i++;
      if (i >= N) {
        mt[0] = mt[N - 1]!;
        i = 1;
      }
    }
    mt[0] = 0x80000000;
  }

  /** `random.seed(a)` pour un entier : la valeur absolue découpée en mots de 32 bits */
  seed(a: bigint): void {
    let n = a < 0n ? -a : a;
    const key: number[] = [];
    while (n > 0n) {
      key.push(Number(n & 0xffffffffn));
      n >>= 32n;
    }
    if (!key.length) key.push(0);
    this.initByArray(key);
  }

  /** un mot de 32 bits */
  next32(): number {
    const mt = this.mt;
    if (this.mti >= N) {
      let kk = 0;
      let y: number;
      for (; kk < N - M; kk++) {
        y = (mt[kk]! & UPPER) | (mt[kk + 1]! & LOWER);
        mt[kk] = mt[kk + M]! ^ (y >>> 1) ^ (y & 1 ? MATRIX_A : 0);
      }
      for (; kk < N - 1; kk++) {
        y = (mt[kk]! & UPPER) | (mt[kk + 1]! & LOWER);
        mt[kk] = mt[kk + (M - N)]! ^ (y >>> 1) ^ (y & 1 ? MATRIX_A : 0);
      }
      y = (mt[N - 1]! & UPPER) | (mt[0]! & LOWER);
      mt[N - 1] = mt[M - 1]! ^ (y >>> 1) ^ (y & 1 ? MATRIX_A : 0);
      this.mti = 0;
    }
    let y = mt[this.mti++]!;
    y ^= y >>> 11;
    y ^= (y << 7) & 0x9d2c5680;
    y ^= (y << 15) & 0xefc60000;
    y ^= y >>> 18;
    return y >>> 0;
  }

  /** `random.random()` : un multiple de 2⁻⁵³ dans [0 ; 1[, sous forme de fraction exacte */
  random(): { n: bigint; d: bigint } {
    const a = BigInt(this.next32() >>> 5);
    const b = BigInt(this.next32() >>> 6);
    return { n: a * 67108864n + b, d: 9007199254740992n };
  }

  /** `getrandbits(k)` */
  getrandbits(k: number): bigint {
    if (k <= 0) return 0n;
    if (k <= 32) return BigInt(this.next32() >>> (32 - k));
    let out = 0n;
    let shift = 0n;
    for (let left = k; left > 0; left -= 32) {
      let r = this.next32();
      if (left < 32) r >>>= 32 - left;
      out |= BigInt(r) << shift;
      shift += 32n;
    }
    return out;
  }

  /** `_randbelow(n)` : un entier de [0 ; n[ (rejet, comme CPython) */
  randbelow(n: bigint): bigint {
    if (n <= 0n) return 0n;
    const k = n.toString(2).length;
    let r = this.getrandbits(k);
    while (r >= n) r = this.getrandbits(k);
    return r;
  }
}
